import { useLayoutEffect, useRef, useState } from 'react';
import { useLanguage } from '../LanguageProvider';
import { useTranslate } from 'react-translate';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [barHeight, setBarHeight] = useState(0);
  const barRef = useRef(null);
  const t = useTranslate('Navbar');

  // The bar is fixed, so a spacer holds its place in the hero layout.
  // Measured before first paint so the page doesn't shift after load.
  useLayoutEffect(() => {
    setBarHeight(barRef.current.offsetHeight);
    const observer = new ResizeObserver(([entry]) =>
      setBarHeight(entry.target.offsetHeight)
    );
    observer.observe(barRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id).scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    setIsOpen(false);
  };

  const links = [
    { name: t('about'), id: 'about' },
    { name: t('tech'), id: 'tech' },
    { name: t('certificates'), id: 'certificates' },
    { name: t('projects'), id: 'projects' },
    { name: t('contact'), id: 'contact' },
  ];

  return (
    <>
      <div aria-hidden='true' style={{ height: barHeight }}></div>
      <header
        ref={barRef}
        className='pointer-events-none fixed inset-x-0 top-0 z-40 px-4 pt-4 md:px-8 md:pt-6'
      >
        <div className='glass-surface pointer-events-auto relative mx-auto flex w-full max-w-6xl items-center justify-between rounded-full px-4 py-3 md:px-6'>
          <button
            type='button'
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className='flex items-center gap-2 text-sm font-extrabold tracking-[0.08em] md:text-lg md:tracking-[0.1em]'
          >
            <span className='brand-dot'></span>
            RAULWEBDEV
          </button>

          <nav className='hidden items-center gap-2 lg:flex'>
            {links.map((link) => (
              <button
                key={link.id}
                type='button'
                onClick={() => scrollToSection(link.id)}
                className='rounded-full px-4 py-2 text-sm font-semibold text-[var(--text-muted)] transition hover:bg-[var(--glass-fill-strong)] hover:text-[var(--text-main)]'
              >
                {link.name}
              </button>
            ))}
            <LanguageSelect className='ml-2' />
          </nav>

          <div className='flex items-center gap-2 lg:hidden'>
            <button
              type='button'
              onClick={() => setIsOpen((open) => !open)}
              className='glass-pill rounded-full p-3 text-[var(--text-main)]'
              aria-label='Toggle menu'
              aria-expanded={isOpen}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-5 w-5'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                {isOpen ? (
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                ) : (
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M4 7h16M4 12h16M10 17h10'
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        <div
          className={`glass-surface absolute left-4 right-4 top-full mt-3 rounded-3xl p-4 transition-all duration-300 lg:hidden ${
            isOpen
              ? 'pointer-events-auto translate-y-0 opacity-100'
              : 'pointer-events-none -translate-y-2 opacity-0'
          }`}
        >
          <div className='flex flex-col gap-2'>
            {links.map((link) => (
              <button
                key={link.id}
                type='button'
                onClick={() => scrollToSection(link.id)}
                className='rounded-2xl px-4 py-3 text-left text-base font-semibold text-[var(--text-muted)] transition hover:bg-[var(--glass-fill-strong)] hover:text-[var(--text-main)]'
              >
                {link.name}
              </button>
            ))}
          </div>
          <div className='mt-3 flex items-center gap-2 border-t border-[var(--border)] pt-3'>
            <LanguageSelect />
          </div>
        </div>
      </header>
    </>
  );
};

const LanguageSelect = ({ className = '' }) => {
  const { lang, setLang } = useLanguage();

  return (
    <div className={`relative ${className}`}>
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        className='glass-pill appearance-none rounded-full pl-4 pr-12 py-2 text-sm font-semibold text-[var(--text-main)] outline-none'
        aria-label='Language selector'
      >
        <option value='en'>English</option>
        <option value='es'>Español</option>
      </select>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 20 20'
        fill='currentColor'
        className='pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-soft)]'
        aria-hidden='true'
      >
        <path
          fillRule='evenodd'
          d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z'
          clipRule='evenodd'
        />
      </svg>
    </div>
  );
};

export default Header;
